import { EditOutlined, SaveOutlined } from "@ant-design/icons";
import { Col, InputNumber, Modal, Row, Select, Spin, Table } from "antd";
import { useFormik } from "formik";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/functional_components/Button/PFButton";
import { setLoading } from "../../redux/ducks/Loading";
import FlatRatePeriod from "../../services/api/FlatRatePeriod";
import { paginationDefaults } from "../../utils/properties";
import { buildQueryString, showError, showSuccess, trimData } from "../../utils/utilities";
import { setFilters } from "../../redux/ducks/Filters";

const FlatRatePeriods = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const [state, setState] = useState({
    tableContent: [],
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    modal: {
      visible: false,
      type: "",
      data: {},
    },
    loading: false,
  });

  useEffect(() => {
    const queryString = buildQueryString(filters.obj, {});
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  }, []);

  const formikFlatRatePeriod = useFormik({
    initialValues: {
      label: get(state.modal.data, "label", null),
      days: get(state.modal.data, "days", null),
      hourStart: get(state.modal.data, "hourStart", null),
      hourEnd: get(state.modal.data, "hourEnd", null),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (values.days === "" || values.days === null || values.days === undefined) {
        // this check to allow 0
        errors.days = "Required!";
      }
      if (values.hourStart === "" || values.hourStart === null || values.hourStart === undefined) {
        // this check to allow 0
        errors.hourStart = "Required!";
      }
      if (values.hourEnd === "" || values.hourEnd === null || values.hourEnd === undefined) {
        // this check to allow 0
        errors.hourEnd = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      const data = {
        ...values,
        id: state.modal.data?.id || undefined,
      };
      const saveResponse = await FlatRatePeriod.setFlatRatePeriod(data);
      if (!saveResponse.error) {
        showSuccess();
        closeModal();
        const queryString = buildQueryString(filters.obj, {});
        dispatch(setFilters(queryString));
        dataList(queryString.string);
      } else {
        showError(get(saveResponse, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const dataList = async (queryString) => {
    dispatch(setLoading(true));
    const { data, error, currentPage, pageSize, totalItems } = await FlatRatePeriod.getFlatRatePeriods(queryString);
    if (!error && data) {
      data.forEach((cc, i) => {
        cc.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: data || [],
        pagination: {
          currentPage: currentPage ? currentPage + 1 : paginationDefaults.page,
          size: pageSize || paginationDefaults.size,
          totalItems: totalItems || paginationDefaults.totalItems,
        },
      }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const closeModal = () => {
    formikFlatRatePeriod.resetForm();
    setState((prevState) => ({ ...prevState, modal: { visible: false, type: "", data: {} } }));
  };

  const editCallback = async (id) => {
    setState((prevState) => ({ ...prevState, loading: true, modal: { visible: true, type: "edit" } }));
    const { data, error } = await FlatRatePeriod.getFlatRatePeriod(id);
    if (!error && data) {
      setState((prevState) => ({ ...prevState, modal: { visible: true, type: "edit", data } }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const columns = [
    {
      title: t("flatRatePeriod.table.hourStart"),
      dataIndex: "hourStart",
      key: "hourStart",
    },
    {
      title: t("flatRatePeriod.table.hourEnd"),
      dataIndex: "hourEnd",
      key: "hourEnd",
    },
    {
      title: t("flatRatePeriod.table.days"),
      dataIndex: "days",
      key: "days",
    },
    {
      title: t("flatRatePeriod.table.label"),
      dataIndex: "label",
      key: "label",
      render: (l) => t(`flatRatePeriod.table.${l}`),
    },
    {
      title: t("general.actions"),
      key: "action",
      width: 80,
      render: (data) => {
        return (
          <div className="space-around">
            <div onClick={() => editCallback(data.id)}>
              <EditOutlined className="icon-medium" />
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Table
        dataSource={state.tableContent}
        columns={columns}
        size="small"
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            const queryString = buildQueryString(filters.obj, { page: page - 1, size: pageSize });
            dispatch(setFilters(queryString));
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                pageSize: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            dataList(queryString.string);
          },
        }}
      />
      <Modal
        open={state.modal.visible}
        width={700}
        title={
          state.modal.type === "edit" && state.modal.data && state.modal.data.brand
            ? state.modal.data.brand
            : t("flatRatePeriod.new")
        }
        footer={[
          <Button
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikFlatRatePeriod.submitForm}
            isDisabled={!(formikFlatRatePeriod.dirty && formikFlatRatePeriod.isValid)}
          />,
        ]}
        onCancel={closeModal}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 24]}>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="label">
                {t("flatRatePeriod.table.label")}*
              </label>
            </Row>
            <Row>
              <Select
                // disabled
                placeholder={t("flatRatePeriod.table.label")}
                id="label"
                name="label"
                className="w-100"
                onChange={(e) => formikFlatRatePeriod.setFieldValue("label", e)}
                onBlur={formikFlatRatePeriod.handleBlur("label")}
                value={formikFlatRatePeriod.values.label}
                options={[
                  { value: "lunch", label: t("flatRatePeriod.table.lunch") },
                  { value: "dinner", label: t("flatRatePeriod.table.dinner") },
                ]}
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="days">
                {t("flatRatePeriod.table.days")}*
              </label>
            </Row>
            <Row>
              <InputNumber
                className="ant-input"
                controls={false}
                required
                id="days"
                name="days"
                onChange={(e) => formikFlatRatePeriod.setFieldValue("days", e)}
                onBlur={formikFlatRatePeriod.handleBlur}
                value={formikFlatRatePeriod.values.days}
                precision={2}
                decimalSeparator=","
              />
            </Row>
          </Col>
        </Row>
        <Row gutter={[40, 24]}>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="hourStart">
                {t("flatRatePeriod.table.hourStart")}*
              </label>
            </Row>
            <Row>
              <InputNumber
                className="ant-input"
                controls={false}
                required
                id="hourStart"
                name="hourStart"
                onChange={(e) => formikFlatRatePeriod.setFieldValue("hourStart", e)}
                onBlur={formikFlatRatePeriod.handleBlur}
                value={formikFlatRatePeriod.values.hourStart}
                precision={2}
                decimalSeparator=","
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="hourEnd">
                {t("flatRatePeriod.table.hourEnd")}*
              </label>
            </Row>
            <Row>
              <InputNumber
                className="ant-input"
                controls={false}
                required
                id="hourEnd"
                name="hourEnd"
                onChange={(e) => formikFlatRatePeriod.setFieldValue("hourEnd", e)}
                onBlur={formikFlatRatePeriod.handleBlur}
                value={formikFlatRatePeriod.values.hourEnd}
                precision={2}
                decimalSeparator=","
              />
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default FlatRatePeriods;
